import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventory extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    closestSection?.addEventListener('change', this.onVariantChange);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    closestSection?.removeEventListener('change', this.onVariantChange);
  }

  onVariantChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const variantPicker = event.target.closest('variant-picker');
    if (!(variantPicker instanceof HTMLElement) || variantPicker.dataset.productId !== this.dataset.productId) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    const variantId = selectedOption?.dataset?.variantId;
    if (!variantId) return;

    this.renderInventoryByVariantId(variantId);
  };

  renderInventoryByVariantId(variantId) {
    const variantInventoryScript = this.querySelector('script[data-variant-inventory]');
    if (!(variantInventoryScript instanceof HTMLScriptElement) || !variantInventoryScript.textContent) return;

    let variantInventoryMap;
    try {
      variantInventoryMap = JSON.parse(variantInventoryScript.textContent);
    } catch {
      return;
    }

    const variantInventory = variantInventoryMap?.[variantId];
    if (!variantInventory) return;

    const inventoryQuantity = Number(variantInventory.inventory_quantity ?? 0);
    const inventoryCapacity = Math.max(Number(this.dataset.inventoryCapacity ?? 50), 1);
    const inventoryManaged = variantInventory.inventory_management === 'shopify';
    const variantAvailable = variantInventory.available !== false;
    const isOutOfStock =
      !variantAvailable ||
      (inventoryManaged && inventoryQuantity <= 0 && variantInventory.inventory_policy !== 'continue');
    const showInventoryQuantity = this.dataset.showInventoryQuantity !== 'false';

    let stockPercent = (inventoryQuantity * 100) / inventoryCapacity;
    stockPercent = Math.max(0, Math.min(100, stockPercent));

    let stockColor = '#009900';
    const stockBg = 'var(--color-background-primary)';
    const stockText = 'var(--color-text-primary)';

    if (inventoryQuantity <= 0) {
      stockColor = '#cc0000';
    } else if (inventoryQuantity <= 5) {
      stockColor = '#e8420a';
    } else if (inventoryQuantity <= 10) {
      stockColor = '#f05f00';
    } else if (inventoryQuantity <= 15) {
      stockColor = '#f08000';
    } else if (inventoryQuantity <= 20) {
      stockColor = '#f0a800';
    } else if (inventoryQuantity <= 25) {
      stockColor = '#e8c800';
    } else if (inventoryQuantity <= 30) {
      stockColor = '#c8d400';
    } else if (inventoryQuantity <= 35) {
      stockColor = '#96c800';
    } else if (inventoryQuantity <= 40) {
      stockColor = '#64b800';
    } else if (inventoryQuantity <= 45) {
      stockColor = '#32a800';
    }

    const badge = this.querySelector('.pdp-stock__badge');
    const dot = this.querySelector('.pdp-stock__dot');
    const label = this.querySelector('.pdp-stock__label');
    const count = this.querySelector('.pdp-stock__count');
    const bar = this.querySelector('.pdp-stock__bar');
    const barFill = this.querySelector('.pdp-stock__bar-fill');

    if (!(badge instanceof HTMLElement) || !(dot instanceof HTMLElement) || !(barFill instanceof HTMLElement)) return;

    if (label instanceof HTMLElement) {
      label.textContent = isOutOfStock ? 'Tükendi' : 'Stokta var';
    }

    if (isOutOfStock) {
      badge.classList.add('pdp-stock__badge--muted');
      dot.classList.add('pdp-stock__dot--muted');
      badge.style.removeProperty('background-color');
      badge.style.removeProperty('color');
      badge.style.removeProperty('border-color');
      dot.style.removeProperty('background-color');
      barFill.style.width = '0%';
    } else {
      badge.classList.remove('pdp-stock__badge--muted');
      dot.classList.remove('pdp-stock__dot--muted');
      badge.style.backgroundColor = stockBg;
      badge.style.color = stockText;
      badge.style.borderColor = stockColor;
      dot.style.backgroundColor = stockColor;
      barFill.style.width = `${stockPercent}%`;
      barFill.style.backgroundColor = stockColor;
    }

    if (count instanceof HTMLElement) {
      const shouldShowCount = inventoryManaged && inventoryQuantity > 0 && showInventoryQuantity && !isOutOfStock;
      if (shouldShowCount) {
        count.hidden = false;
        count.textContent = `${inventoryQuantity} / ${inventoryCapacity} adet kaldı`;
      } else {
        count.hidden = true;
      }
    }

    if (bar instanceof HTMLElement) {
      bar.setAttribute('aria-valuemax', String(inventoryCapacity));
      bar.setAttribute('aria-valuenow', String(Math.max(0, inventoryQuantity)));
    }
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });

    if (event.detail.resource?.id) {
      this.renderInventoryByVariantId(String(event.detail.resource.id));
    }
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
